import {
  connected_record_icon_lookup,
  create_empty_action_form,
  evidence_tag_class_lookup,
  source_label_titles,
  source_meta_lookup,
  source_origin_titles,
  status_class_lookup
} from './corrective_actions_data';
import { action_drawer_catalog } from './corrective_actions_drawer';

function increment_card_metric(card, key) {
  if (!card) return;
  const current_value = Number.parseInt(Reflect.get(card, key), 10);
  if (!Number.isNaN(current_value)) Reflect.set(card, key, String(current_value + 1));
}

function required_trimmed_value(value) {
  const trimmed_value = String(value || '').trim();
  return trimmed_value;
}

function required_field_message(form, key, message) {
  const field_value = Reflect.get(form, key);
  const required_value = required_trimmed_value(field_value);
  return required_value ? '' : message;
}

function validate_new_action_form(form) {
  const field_messages = [
    required_field_message(form, 'action', 'Corrective action is required.'),
    required_field_message(form, 'issue_being_fixed', 'Issue being fixed is required.'),
    required_field_message(form, 'owner', 'Owner is required.')
  ].filter(Boolean);
  if (field_messages.length) return field_messages[0];
  if (!form.due_date) return 'Due date is required.';
  return '';
}

function build_new_action_row(form, new_action_ref, status_label, source_meta) {
  const new_action_row = {
    ref: new_action_ref,
    action: form.action.trim(),
    source: form.source,
    owner: form.owner.trim(),
    due: form.due_date,
    priority: form.priority,
    status: { label: status_label, class: Reflect.get(status_class_lookup, status_label) || status_class_lookup.Open },
    repeat: Boolean(form.root_issue.trim()),
    source_icon: source_meta.icon,
    source_class: source_meta.class,
    issue_being_fixed: form.issue_being_fixed.trim(),
    source_reference: form.source_reference.trim(),
    source_name: form.source_name.trim(),
    root_issue: form.root_issue.trim(),
    escalated: form.escalated
  };
  return new_action_row;
}

function build_action_drawer_entry(form, status_label) {
  const footer_left = form.escalated === 'Yes' ? 'Escalated' : 'Escalate';
  const footer_primary = status_label === 'Awaiting approval' ? 'Closure approval' : 'Advance';
  const action_drawer_entry = {
    connected_records: [],
    evidence_required: [],
    submitted: [],
    closure: { type: 'pending', note: 'Not yet submitted for closure. An action is only closed once an approver verifies the fix against evidence.' },
    activity: [{ actor: form.owner.trim() || 'Action owner', note: 'Corrective action created.', date: new Date().toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) }],
    footer: { left: footer_left, middle: 'Submit evidence', primary: footer_primary }
  };
  return action_drawer_entry;
}

function count_filter_rows(rows, filter) {
  if (filter.id === 'all') {
    const total_count = rows.length;
    return total_count;
  }
  const source_count = rows.filter((row) => row.source === filter.source).length;
  return source_count;
}

function increment_tab_count(tab) {
  if (tab && typeof tab.count === 'number') tab.count += 1;
}

function update_total_actions_card(card, status_label) {
  if (!card) return;
  const current_total = Number.parseInt(card.note, 10);
  if (!Number.isNaN(current_total)) card.note = `${current_total + 1} total`;
  if (status_label !== 'Closed') increment_card_metric(card, 'value');
}

function update_new_action_cards(context, status_label) {
  const total_actions_card = Reflect.get(context.all_action_cards, 0);
  const escalated_card = Reflect.get(context.all_action_cards, 2);
  const awaiting_approval_card = Reflect.get(context.all_action_cards, 3);
  update_total_actions_card(total_actions_card, status_label);
  if (context.new_action_form.escalated === 'Yes') increment_card_metric(escalated_card, 'value');
  if (status_label === 'Awaiting approval') increment_card_metric(awaiting_approval_card, 'value');
}

function create_new_action_flags(form) {
  const action_flags = {
    is_awaiting_approval: form.status === 'Awaiting approval',
    has_root_issue: Boolean(form.root_issue.trim())
  };
  return action_flags;
}

function update_new_action_tabs(context, flags) {
  increment_tab_count(context.tabs.find((tab) => tab.id === 'all-actions'));
  if (flags.is_awaiting_approval) increment_tab_count(context.tabs.find((tab) => tab.id === 'closure-approvals'));
  if (flags.has_root_issue) increment_tab_count(context.tabs.find((tab) => tab.id === 'repeat-detection'));
}

export const corrective_actions_computed = {
  all_action_filters_with_counts() {
    return this.all_action_filters.map((filter) => ({
      ...filter,
      count: count_filter_rows(this.all_action_rows, filter),
      active: this.active_action_filter === filter.id
    }));
  },
  filtered_action_rows() {
    if (this.active_action_filter === 'all') return this.all_action_rows;
    const selected_filter = this.all_action_filters.find((filter) => filter.id === this.active_action_filter);
    if (!selected_filter?.source) return this.all_action_rows;
    return this.all_action_rows.filter((row) => row.source === selected_filter.source);
  },
  next_action_reference() {
    const references = [...this.all_action_rows.map((row) => row.ref), ...this.awaiting_approval_rows.map((row) => row.ref), ...this.recently_closed_rows.map((row) => row.ref), ...this.repeat_groups.flatMap((group) => group.items.map((item) => item.ref))];
    const next_number = references.reduce((highest, ref) => {
      const parsed = Number.parseInt(String(ref).replace('CA-', ''), 10);
      return Number.isNaN(parsed) ? highest : Math.max(highest, parsed);
    }, 0) + 1;
    const next_action_reference = `CA-${String(next_number).padStart(3, '0')}`;
    return next_action_reference;
  }
};

export const corrective_actions_methods = {
  set_tab(tab_id) { this.active_tab = tab_id; this.close_action_drawer(); },
  set_action_filter(filter_id) { this.active_action_filter = filter_id; },
  open_action_drawer(row) { if (row?.ref) this.active_action_drawer_ref = row.ref; },
  open_action_drawer_by_ref(ref) { const row = this.all_action_rows.find((item) => item.ref === ref); if (row) this.open_action_drawer(row); },
  close_action_drawer() { this.active_action_drawer_ref = null; },
  open_new_action_modal() { this.close_action_drawer(); this.new_action_error = ''; this.new_action_form = create_empty_action_form(); this.show_new_action_modal = true; },
  close_new_action_modal() { this.show_new_action_modal = false; this.new_action_error = ''; },
  create_new_action() {
    const validation_error = validate_new_action_form(this.new_action_form);
    if (validation_error) {
      this.new_action_error = validation_error;
      return;
    }
    const source_meta = Reflect.get(source_meta_lookup, this.new_action_form.source) || Reflect.get(source_meta_lookup, 'Audit finding');
    const new_action_ref = this.next_action_reference;
    const status_label = this.new_action_form.status;
    const action_flags = create_new_action_flags(this.new_action_form);
    this.all_action_rows.unshift(build_new_action_row(this.new_action_form, new_action_ref, status_label, source_meta));
    Reflect.set(action_drawer_catalog, new_action_ref, build_action_drawer_entry(this.new_action_form, status_label));
    update_new_action_tabs(this, action_flags);
    update_new_action_cards(this, status_label);
    this.active_action_filter = 'all'; this.close_new_action_modal();
  },
  action_drawer_kicker(row) { return `Corrective action - ${row.ref} - ${String(row.source).toUpperCase()}`; },
  action_status_chip_class(label) {
    return Reflect.get(status_class_lookup, label) || 'bg-light text-secondary';
  },
  action_workflow_chip_class(state) { if (state === 'done') return 'is-done'; if (state === 'current') return 'is-current'; return 'is-upcoming'; },
  repeat_group_summary(group) { if (!group?.items?.length) return group?.subtitle || ''; const open_count = group.items.filter((item) => item.status !== 'Closed').length; return `${group.items.length} actions · ${open_count} still open`; },
  repeat_item_source(ref) { const row = this.all_action_rows.find((item) => item.ref === ref); return row?.source || ''; },
  repeat_item_status_class(status) {
    return Reflect.get(status_class_lookup, status) || 'bg-light text-secondary border border-light';
  },
  workflow_step_icon(state) { if (state === 'done') return 'bi-check-lg'; if (state === 'current') return 'bi-hourglass-split'; return 'bi-dot'; },
  drawer_source_heading(source) {
    return Reflect.get(source_origin_titles, source) || 'Originating source';
  },
  drawer_source_label(source) {
    return Reflect.get(source_label_titles, source) || source;
  },
  connected_record_icon(record_type) {
    return Reflect.get(connected_record_icon_lookup, record_type) || 'bi-link-45deg';
  },
  evidence_tag_class(tag) {
    return Reflect.get(evidence_tag_class_lookup, tag) || 'bg-light text-secondary border border-light';
  },
  footer_action_icon(label) { const action_icon_lookup = { Escalate: 'bi-graph-up-arrow', Escalated: 'bi-flag-fill', 'Submit evidence': 'bi-upload', Reopen: 'bi-arrow-counterclockwise' }; return Reflect.get(action_icon_lookup, label) || ''; },
  source_item_class(tone) { if (tone === 'red') return 'text-danger'; if (tone === 'green') return 'text-success'; return 'text-warning-emphasis'; }
};
